import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import { collection, addDoc, deleteDoc, doc, updateDoc, query, where, getDocs } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useToast } from '../context/ToastContext';
import { useConfirm } from '../context/ConfirmContext';
import VoiceCookingMode from '../components/VoiceCookingMode';

const CATEGORIES = ['כללי', 'איטלקי', 'בשר', 'דגים', 'קינוחים', 'בצקים', 'בישולים'];

const RecipeDetailScreen = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { recipe, isConfirmation, originalUrl, aiCategory, notes: navNotes } = state || {};
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  const [isSaving, setIsSaving] = useState(false);
  const [alreadySaved, setAlreadySaved] = useState(false); // מצב לבדיקת כפילות
  const [selectedCategory, setSelectedCategory] = useState('כללי');
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [notes, setNotes] = useState('');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [recipeImage, setRecipeImage] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [rating, setRating] = useState(0);
  const [showVoiceMode, setShowVoiceMode] = useState(false);

  // בדיקה אם המתכון שייך למשתמש אחר
  const isOtherUserRecipe = recipe?.userId && auth.currentUser && recipe.userId !== auth.currentUser.uid;
  
  const videoUrl = originalUrl || recipe?.originalUrl || '';

  // בדיקה אם המתכון מהקהילה כבר שמור אצלי (לפי URL)
  useEffect(() => {
    const checkIfExists = async () => {
      const user = auth.currentUser;
      if (user && isOtherUserRecipe && videoUrl) {
        try {
          const q = query(
            collection(db, 'recipes'),
            where('userId', '==', user.uid),
            where('originalUrl', '==', videoUrl)
          );
          const snap = await getDocs(q);
          if (!snap.empty) {
            setAlreadySaved(true);
          }
        } catch (error) {
          console.error("Error checking duplication:", error);
        }
      }
    };
    checkIfExists();
  }, [videoUrl, isOtherUserRecipe]);

  useEffect(() => {
    let finalCat = 'כללי';
    if (aiCategory) {
      const clean = String(aiCategory).trim().replace(/['"]/g, '');
      const match = CATEGORIES.find(c => clean.includes(c));
      if (match) finalCat = match;
    } else if (recipe?.category) {
      const match = CATEGORIES.find(c => String(recipe.category).includes(c));
      if (match) finalCat = match;
    }
    setSelectedCategory(finalCat);

    if (recipe) {
      setEditedContent(typeof recipe === 'string' ? recipe : (recipe.content || ''));
      setNotes(navNotes || recipe.notes || '');
      setIsFavorite(recipe.isFavorite || false);
      setIsPinned(recipe.isPinned || false);
      setRecipeImage(recipe.imageUrl || null);
      setRating(recipe.rating || 0);
    }
  }, [aiCategory, recipe, navNotes]);

  if (!recipe) return null;

  const recipeContent = typeof recipe === 'string' ? recipe : editedContent;
  const title = recipeContent.split('\n')[0].replace('#', '').trim();

  const handleSaveToMyBook = async () => {
    const user = auth.currentUser;
    if (!user) { showToast('עליך להיות מחובר כדי לשמור', 'error'); return; }
    setIsSaving(true);
    try {
      await addDoc(collection(db, 'recipes'), {
        userId: user.uid,
        content: editedContent,
        notes: notes,
        category: selectedCategory,
        imageUrl: recipeImage,
        originalUrl: videoUrl,
        date: new Date().toLocaleDateString('he-IL'),
        rating: 0,
        isFavorite: false,
        isPinned: false,
        isShared: isOtherUserRecipe ? true : false
      });
      showToast('המתכון נוסף לספר שלך! ❤️', 'success');
      setAlreadySaved(true); // עדכון מצב מקומי לאחר שמירה
      navigate('/book');
    } catch (e) {
      showToast('שגיאה בשמירת המתכון', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    const user = auth.currentUser;
    if (!user) { showToast('אתה חייב להיות מחובר', 'error'); return; }
    setIsSaving(true);
    try {
      if (isConfirmation) {
        if (videoUrl) {
          const q = query(collection(db, 'recipes'), where('userId', '==', user.uid), where('originalUrl', '==', videoUrl));
          const snap = await getDocs(q);
          if (!snap.empty) { showToast('כבר שמרת מתכון מהלינק הזה!', 'warning'); navigate('/book'); return; }
        }
        await addDoc(collection(db, 'recipes'), {
          userId: user.uid, content: editedContent, category: selectedCategory,
          notes: notes,
          originalUrl: videoUrl, date: new Date().toLocaleDateString('he-IL'),
        });
        showToast('המתכון נשמר בהצלחה! 🎉', 'success');
        navigate('/book');
      } else {
        await updateDoc(doc(db, 'recipes', recipe.id), { 
          content: editedContent, 
          category: selectedCategory,
          notes: notes 
        });
        showToast('השינויים נשמרו ✓', 'success');
        setIsEditing(false);
        setIsEditingNotes(false);
      }
    } catch (e) { showToast('שגיאה בשמירת המתכון', 'error'); }
    finally { setIsSaving(false); }
  };

  const handleDelete = async () => {
    const ok = await confirm('למחוק את המתכון?', {
      subtext: 'פעולה זו לא ניתנת לביטול.',
      confirmLabel: 'מחק מתכון',
      danger: true,
    });
    if (!ok) return;
    try { await deleteDoc(doc(db, 'recipes', recipe.id)); navigate('/book'); }
    catch { showToast('שגיאה במחיקת המתכון', 'error'); }
  };

  const handleShare = async () => {
    const url = `https://cookswithai.com/recipe/${recipe.id}`;
    if (navigator.share) {
      try { await navigator.share({ title, text: 'מתכון מהמם שחילצתי!', url }); } catch { }
    } else {
      try { await navigator.clipboard.writeText(url); showToast('הלינק הועתק! 🔗', 'success'); } catch { }
    }
  };

  const handleToggleFavorite = async () => {
    if (isConfirmation || isOtherUserRecipe || !recipe?.id) return;
    const val = !isFavorite;
    setIsFavorite(val);
    try {
      await updateDoc(doc(db, 'recipes', recipe.id), { isFavorite: val });
      showToast(val ? 'נוסף למועדפים ❤️' : 'הוסר מהמועדפים', val ? 'success' : 'info');
    }
    catch { setIsFavorite(!val); showToast('שגיאה בעדכון המועדף', 'error'); }
  };

  const handleTogglePin = async () => {
    if (isConfirmation || isOtherUserRecipe || !recipe?.id) return;
    const val = !isPinned;
    setIsPinned(val);
    try {
      await updateDoc(doc(db, 'recipes', recipe.id), { isPinned: val });
      showToast(val ? 'המתכון נעוץ 📌' : 'המתכון הוסר מהנעוצים', 'info');
    }
    catch { setIsPinned(!val); showToast('שגיאה בעדכון', 'error'); }
  };

  const handleRating = async (n) => {
    if (isConfirmation || isOtherUserRecipe || !recipe?.id) return;
    setRating(n);
    try { await updateDoc(doc(db, 'recipes', recipe.id), { rating: n }); showToast(`דירגת ${n} כוכבים ⭐`, 'success'); }
    catch { setRating(rating); showToast('שגיאה בדירוג', 'error'); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { showToast('אנא בחר קובץ תמונה', 'error'); return; }
    if (file.size > 5 * 1024 * 1024) { showToast('התמונה גדולה מדי (מקסימום 5MB)', 'warning'); return; }
    if (!recipe?.id) return;
    setIsUploadingImage(true);
    try {
      const storage = getStorage();
      const storageRef = ref(storage, `recipe-images/${recipe.id}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      await updateDoc(doc(db, 'recipes', recipe.id), { imageUrl: url });
      setRecipeImage(url);
      showToast('התמונה הועלתה בהצלחה 📸', 'success');
    } catch { showToast('שגיאה בהעלאת התמונה', 'error'); }
    finally { setIsUploadingImage(false); }
  };

  const handleImageDelete = async () => {
    if (!recipe?.id || !recipeImage) return;
    const ok = await confirm('למחוק את התמונה?', {
      subtext: 'התמונה תוסר מהמתכון לצמיתות.',
      confirmLabel: 'מחק תמונה',
      danger: true,
    });
    if (!ok) return;
    try { await updateDoc(doc(db, 'recipes', recipe.id), { imageUrl: null }); setRecipeImage(null); showToast('התמונה נמחקה', 'info'); }
    catch { showToast('שגיאה במחיקת התמונה', 'error'); }
  };

  return (
    <div className="min-h-screen bg-background text-on-surface font-body pb-32" dir="rtl">

      {showVoiceMode && (
        <VoiceCookingMode
          recipe={{ ...recipe, content: recipeContent }}
          onClose={() => setShowVoiceMode(false)}
        />
      )}

      <header className="sticky top-0 z-50 bg-[#f9f9f9]/80 backdrop-blur-md px-6 py-4 flex justify-between items-center">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-surface-container transition-colors active:scale-95">
          <span className="material-symbols-outlined text-primary">arrow_forward</span>
        </button>
        <h1 className="font-headline font-bold text-lg text-primary text-center flex-1 mx-2">
          {isConfirmation ? 'אישור מתכון מה-AI' : isOtherUserRecipe ? 'מתכון מהקהילה' : 'פרטי המתכון'}
        </h1>
        {!isConfirmation && !isEditing && (
          <button onClick={handleShare} className="p-2 rounded-full hover:bg-surface-container transition-colors active:scale-95">
            <span className="material-symbols-outlined text-primary">share</span>
          </button>
        )}
        {(isConfirmation || isEditing) && <div className="w-10" />}
      </header>

      <main className="max-w-screen-xl mx-auto px-6 pt-4">

        {/* תמונה */}
        {recipeImage ? (
          <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden shadow-xl mb-6">
            <img src={recipeImage} alt={title} className="w-full h-full object-cover" />
            {!isOtherUserRecipe && (
              <div className="absolute top-3 left-3 flex gap-2">
                <label className="px-3 py-1.5 bg-white/80 backdrop-blur rounded-full text-xs font-bold text-on-surface cursor-pointer hover:bg-white transition-all">
                  🔄 שנה
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={isUploadingImage} />
                </label>
                <button onClick={handleImageDelete} className="px-3 py-1.5 bg-white/80 backdrop-blur rounded-full text-xs font-bold text-error hover:bg-white transition-all">
                  🗑️ מחק
                </button>
              </div>
            )}
          </div>
        ) : (!isConfirmation && !isEditing && !isOtherUserRecipe) && (
          <label className="flex items-center justify-center gap-2 w-full py-5 bg-surface-container-low border-2 border-dashed border-outline-variant/40 rounded-xl mb-6 cursor-pointer hover:bg-surface-container transition-colors">
            <span className="material-symbols-outlined text-primary">add_a_photo</span>
            <span className="font-label font-semibold text-on-surface-variant text-sm">
              {isUploadingImage ? 'מעלה תמונה...' : 'הוסף תמונה למתכון'}
            </span>
            <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={isUploadingImage} />
          </label>
        )}

        <h2 className="text-3xl font-headline font-extrabold tracking-tight mb-2 leading-tight">{title}</h2>

        {/* לוגיקת כפתור שמירה מהקהילה או חיווי שכבר נשמר */}
        {isOtherUserRecipe && (
          <div className="mb-6 animate-in slide-in-from-top duration-500">
            {alreadySaved ? (
              <div className="w-full py-4 bg-surface-container-high text-primary rounded-2xl font-bold flex items-center justify-center gap-2 border border-primary/20">
                <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                המתכון הזה כבר שמור בספר שלך
              </div>
            ) : (
              <button
                onClick={handleSaveToMyBook}
                disabled={isSaving}
                className="w-full py-4 bg-tertiary text-on-tertiary rounded-2xl font-bold flex items-center justify-center gap-2 shadow-lg active:scale-95 transition-transform"
              >
                <span className="material-symbols-outlined">library_add</span>
                {isSaving ? 'שומר...' : 'אהבת? שמור לספר המתכונים שלך'}
              </button>
            )}
          </div>
        )}

        {/* שורת פעולות (רק למתכון שלי) */}
        {!isConfirmation && !isEditing && !isOtherUserRecipe && (
          <div className="flex items-center justify-between mb-6">
            <div className="flex gap-2">
              <button onClick={handleTogglePin} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all active:scale-90 ${isPinned ? 'bg-primary text-white' : 'bg-surface-container text-on-surface-variant'}`}>
                <span className="material-symbols-outlined text-sm" style={isPinned ? { fontVariationSettings: "'FILL' 1" } : {}}>push_pin</span>
                {isPinned ? 'נעוץ' : 'נעץ'}
              </button>
              <button onClick={handleToggleFavorite} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all active:scale-90 ${isFavorite ? 'bg-error text-white' : 'bg-surface-container text-on-surface-variant'}`}>
                <span className="material-symbols-outlined text-sm" style={isFavorite ? { fontVariationSettings: "'FILL' 1" } : {}}>favorite</span>
                {isFavorite ? 'מועדף' : 'הוסף'}
              </button>
            </div>
          </div>
        )}

        {/* קטגוריה */}
        {(isEditing || isConfirmation) ? (
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-6">
            {CATEGORIES.map(cat => (
              <button key={cat} onClick={() => setSelectedCategory(cat)}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-label font-semibold transition-all cursor-pointer active:scale-95 ${selectedCategory === cat ? 'bg-primary text-white shadow-md shadow-primary/20' : 'bg-surface-container text-on-surface-variant'}`}
              >
                {cat}
              </button>
            ))}
          </div>
        ) : (
          <div className="mb-6">
            <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-primary/10 text-primary text-sm font-bold rounded-full">
              <span className="material-symbols-outlined text-base">category</span>
              {selectedCategory}
            </span>
          </div>
        )}

        {videoUrl && (
          <a href={videoUrl} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-3 px-5 py-3 bg-surface-container-low rounded-xl mb-6 hover:bg-surface-container transition-colors">
            <span className="material-symbols-outlined text-primary">play_circle</span>
            <span className="font-semibold text-primary text-sm">צפה בסרטון המקורי 🎬</span>
          </a>
        )}

        {/* כרטיסיית תוכן */}
        <div className="recipe-container bg-surface-container-lowest rounded-xl recipe-card-shadow p-6 mb-6">
          {!isConfirmation && !isEditing && !isOtherUserRecipe && (
            <div className="flex items-center gap-2 mb-5 pb-4 border-b border-outline-variant/20">
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map(s => (
                  <button key={s} onClick={() => handleRating(s)} className="transition-transform hover:scale-110 active:scale-90">
                    <span className="material-symbols-outlined text-lg" style={{ color: s <= rating ? '#F59E0B' : '#D1D5DB', fontVariationSettings: s <= rating ? "'FILL' 1" : "'FILL' 0" }}>star</span>
                  </button>
                ))}
              </div>
              <button onClick={() => setIsEditing(true)} className="flex items-center gap-1.5 px-4 py-2 bg-surface-container rounded-full text-xs font-bold text-on-surface hover:bg-surface-container-high transition-all active:scale-90 mr-auto">
                <span className="material-symbols-outlined text-sm">edit</span> ערוך
              </button>
              <button onClick={handleDelete} className="flex items-center gap-1.5 px-4 py-2 bg-error/10 rounded-full text-xs font-bold text-error hover:bg-error/20 transition-all active:scale-90">
                <span className="material-symbols-outlined text-sm">delete</span> מחק
              </button>
            </div>
          )}

          {isOtherUserRecipe && rating > 0 && (
            <div className="flex items-center gap-1 mb-4 opacity-70">
              <span className="material-symbols-outlined text-amber-500 text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
              <span className="text-xs font-bold">מתכון בדירוג {rating} כוכבים</span>
            </div>
          )}

          {isEditing ? (
            <textarea value={editedContent} onChange={e => setEditedContent(e.target.value)}
              className="w-full min-h-[300px] bg-surface-container-low border-none outline-none ring-1 ring-outline-variant/30 focus:ring-4 focus:ring-primary/15 rounded-xl p-4 text-on-surface resize-none transition-all" />
          ) : (
            <pre className="whitespace-pre-wrap word-break-all text-base leading-relaxed text-on-surface font-body m-0">
              {recipeContent}
            </pre>
          )}
        </div>

        {/* הערות אישיות */}
        {!isConfirmation && !isOtherUserRecipe && (
          <div className="bg-surface-container-lowest rounded-xl recipe-card-shadow p-6 mb-6 border border-primary/10">
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-outline-variant/20">
              <div className="flex items-center gap-2 text-primary">
                <span className="material-symbols-outlined font-variation-fill">sticky_note_2</span>
                <h3 className="font-headline font-bold">הערות אישיות</h3>
              </div>
              {!isEditingNotes ? (
                <button 
                  onClick={() => setIsEditingNotes(true)}
                  className="text-xs font-bold text-primary px-3 py-1.5 bg-primary/5 rounded-full hover:bg-primary/10 transition-colors"
                >
                  ערוך הערות
                </button>
              ) : (
                <div className="flex gap-2">
                  <button 
                    onClick={() => { setIsEditingNotes(false); setNotes(recipe.notes || ''); }}
                    className="text-xs font-bold text-outline-variant px-3 py-1.5"
                  >
                    ביטול
                  </button>
                  <button 
                    onClick={handleSave}
                    className="text-xs font-bold text-primary px-4 py-1.5 bg-primary/10 rounded-full"
                  >
                    שמור
                  </button>
                </div>
              )}
            </div>
            
            {isEditingNotes || isEditing ? (
              <textarea 
                value={notes} 
                onChange={e => setNotes(e.target.value)}
                placeholder="כתוב כאן הערות לעצמך (שינויים, טיפים וכו')..."
                className="w-full min-h-[100px] bg-surface-container-low border-none outline-none ring-1 ring-outline-variant/30 focus:ring-4 focus:ring-primary/15 rounded-xl p-4 text-on-surface resize-none transition-all text-sm leading-relaxed" 
              />
            ) : (
              <div className="min-h-[60px]">
                {notes ? (
                  <p className="text-sm text-on-surface-variant leading-relaxed whitespace-pre-wrap italic">
                    {notes}
                  </p>
                ) : (
                  <button 
                    onClick={() => setIsEditingNotes(true)}
                    className="w-full py-4 text-sm text-outline-variant font-medium dashed-border rounded-xl hover:bg-surface-container-low transition-colors"
                    style={{ border: '2px dashed currentColor' }}
                  >
                    + הוסף הערה אישית למתכון
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {(isConfirmation || isEditing) && !isOtherUserRecipe && (
          <button onClick={handleSave} disabled={isSaving}
            className={`w-full py-5 rounded-full font-label font-bold text-white text-lg transition-all active:scale-95 ${isSaving ? 'bg-outline-variant cursor-not-allowed' : 'bg-gradient-to-r from-primary to-primary-container shadow-lg shadow-primary/20'}`}>
            {isSaving ? 'שומר...' : isEditing ? 'שמור שינויים 💾' : 'שמור לספר המתכונים 📖'}
          </button>
        )}

      </main>
    </div>
  );
};

export default RecipeDetailScreen;